import React, { memo, useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import { Portal, IconButton, CircularProgress } from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import {
    Save as SaveIcon,
    Search as SearchIcon,
    DeleteOutline as DeleteOutlineIcon,
    EditOutlined as EditIcon,
    Add as AddIcon,
    EventRepeat as ArrangementIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import masavService from '../../../Services/masav.service';
import CustomMonthDatePicker from '../../Widgets/CustomDates/CustomMonthDatePicker/CustomMonthDatePicker';
import { beautifyNumber } from '../../../Utils/FormatNumber';
import s from './PaymentArrangementsModal.module.scss';

const PAGE_SIZE = 60;
const FILTER_ALL = 'all';
const FILTER_WITH = 'with';
const FILTER_WITHOUT = 'without';

function pickArrangement(raw) {
    return {
        id: raw.id ?? raw.Id ?? null,
        caseId: raw.caseId ?? raw.CaseId ?? '',
        clientName: raw.clientName ?? raw.ClientName ?? '',
        accountantId: raw.accountantId ?? raw.AccountantId ?? '',
        startYear: Number(raw.startYear ?? raw.StartYear ?? 0) || '',
        startMonth: Number(raw.startMonth ?? raw.StartMonth ?? 0) || '',
        endYear: Number(raw.endYear ?? raw.EndYear ?? 0) || '',
        endMonth: Number(raw.endMonth ?? raw.EndMonth ?? 0) || '',
        monthlyAmount: raw.monthlyAmount ?? raw.MonthlyAmount ?? '',
        isActive: !!(raw.isActive ?? raw.IsActive),
        notes: raw.notes ?? raw.Notes ?? '',
    };
}

const clearedArrangement = (row) => ({
    ...row,
    isActive: false,
    id: null,
    startYear: '',
    startMonth: '',
    endYear: '',
    endMonth: '',
    monthlyAmount: '',
    notes: '',
});

const monthIndex = (year, month) => Number(year) * 12 + (Number(month) - 1);
const toUtcMonthStart = (year, month) => new Date(Date.UTC(Number(year), Number(month) - 1, 1));
const formatMonth = (year, month) => (year && month ? `${String(month).padStart(2, '0')}/${year}` : '—');

const currentMonthIndex = () => {
    const now = new Date();
    return monthIndex(now.getFullYear(), now.getMonth() + 1);
};

const monthsInRange = (r) =>
    r.isActive && r.startYear && r.endYear
        ? monthIndex(r.endYear, r.endMonth) - monthIndex(r.startYear, r.startMonth) + 1
        : 0;

/** 'none' (no arrangement), 'upcoming', 'active' (covers this month) or 'ended'. */
const statusOf = (r, nowIdx) => {
    if (!r.isActive) return 'none';
    if (monthIndex(r.endYear, r.endMonth) < nowIdx) return 'ended';
    if (monthIndex(r.startYear, r.startMonth) > nowIdx) return 'upcoming';
    return 'active';
};

const STATUS_CHIP = {
    active: { className: s.chipActive, key: 'Active' },
    upcoming: { className: s.chipUpcoming, key: 'PaymentArrangementUpcoming' },
    ended: { className: s.chipEnded, key: 'PaymentArrangementEnded' },
    none: { className: '', key: 'PaymentArrangementNone' },
};

function draftFromRow(row) {
    const now = new Date();
    const fallback = toUtcMonthStart(now.getFullYear(), now.getMonth() + 1);
    return {
        start: row.isActive ? toUtcMonthStart(row.startYear, row.startMonth) : fallback,
        end: row.isActive ? toUtcMonthStart(row.endYear, row.endMonth) : fallback,
        monthlyAmount: row.isActive && row.monthlyAmount !== '' ? String(row.monthlyAmount) : '',
        notes: row.notes || '',
    };
}

const draftMonths = (d) =>
    d.start && d.end
        ? monthIndex(d.end.getUTCFullYear(), d.end.getUTCMonth() + 1) -
          monthIndex(d.start.getUTCFullYear(), d.start.getUTCMonth() + 1) +
          1
        : 0;

const ArrangementRow = memo(function ArrangementRow({ row, status, busy, isEditing, onEdit, onClear, t }) {
    const months = monthsInRange(row);
    const chip = STATUS_CHIP[status];
    return (
        <tr className={isEditing ? s.rowEditing : undefined}>
            <td>
                <div className={s.clientCell}>
                    <span className={s.clientName}>{row.clientName}</span>
                    <span className={s.clientCase}>{row.caseId}</span>
                </div>
            </td>
            <td>
                {row.isActive ? (
                    <span className={s.period}>
                        {formatMonth(row.startYear, row.startMonth)}
                        <span>–</span>
                        {formatMonth(row.endYear, row.endMonth)}
                        <span className={s.periodMonths}>({t('PaymentArrangementMonths', { count: months })})</span>
                    </span>
                ) : (
                    <span className={s.muted}>—</span>
                )}
            </td>
            <td className={s.num}>{row.isActive ? beautifyNumber(row.monthlyAmount) : '—'}</td>
            <td className={s.num}>{row.isActive ? beautifyNumber(Number(row.monthlyAmount) * months) : '—'}</td>
            <td>
                <span className={`${s.chip} ${chip.className}`}>{t(chip.key)}</span>
            </td>
            <td>
                <div className={s.actions}>
                    <button
                        type="button"
                        className={`${row.isActive ? s.secondaryBtn : s.primaryBtn} ${s.smallBtn}`}
                        disabled={busy}
                        onClick={() => onEdit(row.caseId)}
                    >
                        {row.isActive ? <EditIcon fontSize="inherit" /> : <AddIcon fontSize="inherit" />}
                        {row.isActive ? t('Edit') : t('PaymentArrangementAdd')}
                    </button>
                    <button
                        type="button"
                        className={`${s.dangerBtn} ${s.smallBtn}`}
                        disabled={busy || !row.isActive}
                        onClick={() => onClear(row.caseId)}
                    >
                        {busy ? <CircularProgress size={12} color="inherit" /> : <DeleteOutlineIcon fontSize="inherit" />}
                        {t('Clear')}
                    </button>
                </div>
            </td>
        </tr>
    );
});

/** Holds its own draft so typing re-renders only the editor, not the whole table. */
function ArrangementEditor({ row, saving, onSave, onCancel, t, direction }) {
    const [draft, setDraft] = useState(() => draftFromRow(row));
    const months = draftMonths(draft);
    const amount = Number(draft.monthlyAmount);
    const rangeInvalid = months <= 0;
    const canSave = !rangeInvalid && amount > 0 && !saving;

    const set = (patch) => setDraft((prev) => ({ ...prev, ...patch }));

    return (
        <tr>
            <td colSpan={6} className={s.editorCell}>
                <div className={s.editor} dir={direction}>
                    <div className={s.field}>
                        <span className={s.fieldLabel}>{t('PaymentArrangementStartMonth')}</span>
                        <CustomMonthDatePicker
                            date={draft.start}
                            onDateChange={(d) => d && set({ start: d })}
                            direction={direction}
                            height="40px"
                        />
                    </div>
                    <div className={s.field}>
                        <span className={s.fieldLabel}>{t('PaymentArrangementEndMonth')}</span>
                        <CustomMonthDatePicker
                            date={draft.end}
                            onDateChange={(d) => d && set({ end: d })}
                            direction={direction}
                            height="40px"
                        />
                    </div>
                    <div className={s.field}>
                        <span className={s.fieldLabel}>{t('PaymentArrangementAmount')}</span>
                        <input
                            className={`${s.input} ${s.inputNum}`}
                            type="number"
                            min="0"
                            step="0.01"
                            value={draft.monthlyAmount}
                            onChange={(e) => set({ monthlyAmount: e.target.value })}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && canSave) onSave(row.caseId, draft);
                                if (e.key === 'Escape') onCancel();
                            }}
                            autoFocus
                        />
                    </div>
                    <div className={s.field}>
                        <span className={s.fieldLabel}>{t('Notes')}</span>
                        <input
                            className={s.input}
                            value={draft.notes}
                            onChange={(e) => set({ notes: e.target.value })}
                            maxLength={500}
                        />
                    </div>
                    <div className={s.editorFooter}>
                        {rangeInvalid ? (
                            <span className={s.editorError}>{t('PaymentArrangementRangeInvalid')}</span>
                        ) : (
                            <span className={s.editorSummary}>
                                {t('PaymentArrangementSummary', {
                                    months,
                                    amount: beautifyNumber(amount > 0 ? amount : 0),
                                    total: beautifyNumber(amount > 0 ? amount * months : 0),
                                })}
                            </span>
                        )}
                        <button type="button" className={s.ghostBtn} onClick={onCancel} disabled={saving}>
                            {t('Cancel')}
                        </button>
                        <button
                            type="button"
                            className={s.primaryBtn}
                            disabled={!canSave}
                            onClick={() => onSave(row.caseId, draft)}
                        >
                            {saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon fontSize="small" />}
                            {t('Save')}
                        </button>
                    </div>
                </div>
            </td>
        </tr>
    );
}

/**
 * Manage per-client collection payment arrangements (start–end month + monthly amount).
 * Masav / CC bulk charge use this amount instead of full balance while inside the range.
 */
const PaymentArrangementsModal = ({
    open,
    onClose,
    accountantId,
    direction = 'rtl',
    /** Optional case-id filter from Collection page selection. */
    filterCaseIds,
    onChanged,
}) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [rows, setRows] = useState([]);
    const [editingCaseId, setEditingCaseId] = useState(null);
    const [savingCaseId, setSavingCaseId] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [filter, setFilter] = useState(FILTER_ALL);
    const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
    const deferredQuery = useDeferredValue(searchQuery);
    const loadSeqRef = useRef(0);
    const scrollRef = useRef(null);
    const sentinelRef = useRef(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const nowIdx = useMemo(() => currentMonthIndex(), [open]);
    const onChangedRef = useRef(onChanged);
    onChangedRef.current = onChanged;

    const load = async () => {
        if (!accountantId) return;
        const seq = ++loadSeqRef.current;
        setLoading(true);
        setError(null);
        try {
            const data = await masavService.getPaymentArrangements(
                accountantId,
                Array.isArray(filterCaseIds) && filterCaseIds.length > 0 ? filterCaseIds : undefined
            );
            if (seq !== loadSeqRef.current) return;
            setRows((data.arrangements ?? data.Arrangements ?? []).map(pickArrangement));
        } catch (e) {
            if (seq !== loadSeqRef.current) return;
            console.error(e);
            setError(e.message || 'Error');
        } finally {
            if (seq === loadSeqRef.current) setLoading(false);
        }
    };

    useEffect(() => {
        if (!open || !accountantId) return;
        setEditingCaseId(null);
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, accountantId, filterCaseIds]);

    const stats = useMemo(() => {
        const acc = { total: rows.length, withArr: 0, monthlyTotal: 0, ended: 0 };
        rows.forEach((r) => {
            if (!r.isActive) return;
            acc.withArr += 1;
            const status = statusOf(r, nowIdx);
            if (status === 'ended') acc.ended += 1;
            if (status === 'active') acc.monthlyTotal += Number(r.monthlyAmount) || 0;
        });
        return acc;
    }, [rows, nowIdx]);

    const filteredRows = useMemo(() => {
        const q = String(deferredQuery || '').trim().toLowerCase();
        return rows.filter((r) => {
            if (filter === FILTER_WITH && !r.isActive) return false;
            if (filter === FILTER_WITHOUT && r.isActive) return false;
            if (!q) return true;
            return (
                String(r.clientName || '').toLowerCase().includes(q) ||
                String(r.caseId || '').toLowerCase().includes(q)
            );
        });
    }, [rows, deferredQuery, filter]);

    useEffect(() => {
        setVisibleCount(PAGE_SIZE);
        if (scrollRef.current) scrollRef.current.scrollTop = 0;
    }, [deferredQuery, filter, rows.length]);

    const visibleRows = filteredRows.slice(0, visibleCount);
    const hasMore = visibleCount < filteredRows.length;

    useEffect(() => {
        if (!hasMore || !sentinelRef.current || !scrollRef.current) return undefined;
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries.some((e) => e.isIntersecting)) setVisibleCount((n) => n + PAGE_SIZE);
            },
            { root: scrollRef.current, rootMargin: '200px' }
        );
        observer.observe(sentinelRef.current);
        return () => observer.disconnect();
    }, [hasMore, visibleRows.length]);

    const handleEdit = useCallback((caseId) => setEditingCaseId(caseId), []);
    const handleCancelEdit = useCallback(() => setEditingCaseId(null), []);

    const handleSave = async (caseId, d) => {
        const startYear = d.start.getUTCFullYear();
        const startMonth = d.start.getUTCMonth() + 1;
        const endYear = d.end.getUTCFullYear();
        const endMonth = d.end.getUTCMonth() + 1;
        const amount = Number(d.monthlyAmount);
        if (!(amount > 0) || monthIndex(endYear, endMonth) < monthIndex(startYear, startMonth)) {
            toast.error(t('PaymentArrangementInvalid'));
            return;
        }
        setSavingCaseId(caseId);
        try {
            const saved = await masavService.upsertPaymentArrangement({
                caseId,
                accountantId,
                startYear,
                startMonth,
                endYear,
                endMonth,
                monthlyAmount: amount,
                notes: d.notes?.trim() || null,
                isActive: true,
            });
            const mapped = pickArrangement(saved);
            setRows((prev) =>
                prev.map((r) => (r.caseId === caseId ? { ...r, ...mapped, clientName: r.clientName, isActive: true } : r))
            );
            setEditingCaseId(null);
            toast.success(t('PaymentArrangementSaved'));
            onChanged?.();
        } catch (e) {
            console.error(e);
            toast.error(e.message || t('SaveFailed'));
        } finally {
            setSavingCaseId(null);
        }
    };

    const rowsRef = useRef(rows);
    rowsRef.current = rows;

    const handleClear = useCallback(
        async (caseId) => {
            const row = rowsRef.current.find((r) => r.caseId === caseId);
            const confirm = await Swal.fire({
                icon: 'warning',
                title: t('PaymentArrangementClearConfirmTitle'),
                text: t('PaymentArrangementClearConfirmText', { name: row?.clientName || caseId }),
                showCancelButton: true,
                confirmButtonText: t('Clear'),
                cancelButtonText: t('Cancel'),
                confirmButtonColor: '#e11d48',
                reverseButtons: direction === 'rtl',
            });
            if (!confirm.isConfirmed) return;
            setSavingCaseId(caseId);
            try {
                await masavService.deactivatePaymentArrangement(accountantId, caseId);
                setRows((prev) => prev.map((r) => (r.caseId === caseId ? clearedArrangement(r) : r)));
                setEditingCaseId((cur) => (cur === caseId ? null : cur));
                toast.success(t('PaymentArrangementCleared'));
                onChangedRef.current?.();
            } catch (e) {
                console.error(e);
                toast.error(e.message || t('SaveFailed'));
            } finally {
                setSavingCaseId(null);
            }
        },
        [accountantId, direction, t]
    );

    if (!open) return null;

    const filterOptions = [
        { id: FILTER_ALL, label: t('All'), count: stats.total },
        { id: FILTER_WITH, label: t('PaymentArrangementFilterWith'), count: stats.withArr },
        { id: FILTER_WITHOUT, label: t('PaymentArrangementFilterWithout'), count: stats.total - stats.withArr },
    ];

    return (
        <Portal>
            <div className={s.overlay} onClick={onClose}>
                <div dir={direction} onClick={(e) => e.stopPropagation()}>
                    <div className={s.modal}>
                        <div className={s.body}>
                            <div className={s.header}>
                                <IconButton size="small" onClick={onClose} aria-label="close">
                                    <CancelIcon className={s.closeIcon} />
                                </IconButton>
                                <div className={s.headerText}>
                                    <span className={s.headerTitle}>
                                        <ArrangementIcon />
                                        {t('PaymentArrangementsTitle')}
                                    </span>
                                    <p className={s.headerSubtitle}>{t('PaymentArrangementsHint')}</p>
                                </div>
                                <div style={{ width: 34 }} />
                            </div>

                            <div className={s.stats}>
                                <div className={s.statCard}>
                                    <span className={s.statLabel}>{t('PaymentArrangementStatClients')}</span>
                                    <span className={s.statValue}>{stats.total}</span>
                                </div>
                                <div className={s.statCard}>
                                    <span className={s.statLabel}>{t('PaymentArrangementStatWith')}</span>
                                    <span className={s.statValue}>{stats.withArr}</span>
                                </div>
                                <div className={s.statCard}>
                                    <span className={s.statLabel}>{t('PaymentArrangementStatMonthly')}</span>
                                    <span className={s.statValue}>{beautifyNumber(stats.monthlyTotal)}</span>
                                </div>
                                <div className={`${s.statCard}${stats.ended > 0 ? ` ${s.statCardAccent}` : ''}`}>
                                    <span className={s.statLabel}>{t('PaymentArrangementStatEnded')}</span>
                                    <span className={s.statValue}>{stats.ended}</span>
                                </div>
                            </div>

                            <div className={s.toolbar}>
                                <label className={s.search}>
                                    <SearchIcon className={s.searchIcon} />
                                    <input
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        placeholder={t('MasavDraftSearchRowsPlaceholder')}
                                    />
                                </label>
                                <div className={s.filters}>
                                    {filterOptions.map((f) => (
                                        <button
                                            key={f.id}
                                            type="button"
                                            className={`${s.filterBtn}${filter === f.id ? ` ${s.filterBtnActive}` : ''}`}
                                            onClick={() => setFilter(f.id)}
                                        >
                                            {f.label}
                                            <span className={s.filterCount}>{f.count}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {error && !loading && <div className={s.errorBox}>{error}</div>}

                            <div className={s.tableWrap} ref={scrollRef}>
                                {loading ? (
                                    <div className={s.loadingState}>
                                        <CircularProgress size={30} />
                                    </div>
                                ) : (
                                    <table className={s.table} dir={direction}>
                                        <thead>
                                            <tr>
                                                <th>{t('ClientName')}</th>
                                                <th>{t('PaymentArrangementPeriod')}</th>
                                                <th className={s.num}>{t('PaymentArrangementAmount')}</th>
                                                <th className={s.num}>{t('PaymentArrangementTotal')}</th>
                                                <th>{t('Status')}</th>
                                                <th />
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {visibleRows.map((r) => (
                                                <React.Fragment key={r.caseId}>
                                                    <ArrangementRow
                                                        row={r}
                                                        status={statusOf(r, nowIdx)}
                                                        busy={savingCaseId === r.caseId}
                                                        isEditing={editingCaseId === r.caseId}
                                                        onEdit={handleEdit}
                                                        onClear={handleClear}
                                                        t={t}
                                                    />
                                                    {editingCaseId === r.caseId && (
                                                        <ArrangementEditor
                                                            row={r}
                                                            saving={savingCaseId === r.caseId}
                                                            onSave={handleSave}
                                                            onCancel={handleCancelEdit}
                                                            t={t}
                                                            direction={direction}
                                                        />
                                                    )}
                                                </React.Fragment>
                                            ))}
                                            {filteredRows.length === 0 && (
                                                <tr>
                                                    <td colSpan={6}>
                                                        <div className={s.emptyState}>
                                                            {t('NoResultsFound') || t('NoData')}
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                )}
                                {!loading && hasMore && (
                                    <div ref={sentinelRef} className={s.sentinel}>
                                        <CircularProgress size={18} />
                                    </div>
                                )}
                            </div>

                            <div className={s.footer}>
                                <span className={s.muted}>
                                    {t('PaymentArrangementShowing', {
                                        shown: Math.min(visibleCount, filteredRows.length),
                                        total: filteredRows.length,
                                    })}
                                </span>
                                <div className={s.footerSpacer} />
                                <button type="button" className={s.secondaryBtn} onClick={onClose}>
                                    {t('Close')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Portal>
    );
};

export default PaymentArrangementsModal;
